import { supabase, syncConfigured } from "./supabaseClient.js";
import {
  KDF_ITERATIONS, isEnvelope, passphraseProblem, randomSalt, deriveKey, encryptJson, decryptJson,
  getKeyRecord, setKeyRecord, deleteKeyRecord, hasEncryptionFlag, setEncryptionFlag,
} from "./e2ee.js";

// Every function below is a no-op (returns a clear "not configured" error)
// until supabaseClient.js has real keys in it. This keeps the app safe to
// ship even before you've set up Supabase.

export async function signUp(email, password, phone, consent = {}) {
  if (!syncConfigured) return { data: null, error: { message: "Sync isn't set up yet." } };
  return supabase.auth.signUp({
    email,
    password,
    options: { data: { phone, ...consent } },   // consent = which Terms and Privacy Policy versions were accepted, and when
  });
}

export async function signIn(email, password) {
  if (!syncConfigured) return { data: null, error: { message: "Sync isn't set up yet." } };
  return supabase.auth.signInWithPassword({ email, password });
}

export async function signOut() {
  if (!syncConfigured) return;
  await supabase.auth.signOut();
}

export async function getSession() {
  if (!syncConfigured) return null;
  const { data } = await supabase.auth.getSession();
  return data.session;
}

// Forgot password: emails a link. Opening it brings the person back here signed in "for recovery", and the app then asks
// for a new password (see ResetPasswordModal). The redirect must be listed under Authentication > URL Configuration in Supabase.
export async function sendPasswordReset(email) {
  if (!syncConfigured) return { error: { message: "Sync isn't set up yet." } };
  return supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}${window.location.pathname}` });
}
export async function updatePassword(newPassword) {
  if (!syncConfigured) return { error: { message: "Sync isn't set up yet." } };
  return supabase.auth.updateUser({ password: newPassword });
}

// Forgot app-lock PIN: proves the person controls the account's email by sending a one-time code to it. Never creates an account.
// The code appears in the email only if the "Magic Link" email template includes {{ .Token }} (see docs/auth-setup.md).
export async function sendLockResetCode(email) {
  if (!syncConfigured) return { error: { message: "Sync isn't set up yet." } };
  return supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: false } });
}
export async function verifyLockResetCode(email, token) {
  if (!syncConfigured) return { error: { message: "Sync isn't set up yet." } };
  return supabase.auth.verifyOtp({ email, token, type: "email" });
}

// ---- knowing when another device has changed the data --------------------------------------------------------------
// Each save stamps the row with updated_at. We remember the stamp of the last copy we pushed or pulled; if the row now carries a
// different stamp, someone else changed it and it's time to pull.
const stamps = new Map();
export const hasStamp = (userId) => stamps.has(userId);
const sameInstant = (a, b) => Date.parse(a) === Date.parse(b);

export async function remoteChanged(userId) {
  if (!syncConfigured) return false;
  const { data, error } = await supabase.from("user_data").select("updated_at").eq("user_id", userId).maybeSingle();
  if (error || !data) return false;
  const mine = stamps.get(userId);
  return !mine || !sameInstant(data.updated_at, mine);
}

// Live updates: Supabase Realtime tells us when this account's row changes. It needs the table added to the realtime publication
// (see supabase-schema.sql); without that, the app still catches up through its regular checks.
export function subscribeToRemoteChanges(userId, onChange) {
  if (!syncConfigured) return () => {};
  const channel = supabase.channel(`bb-sync-${userId}`)
    .on("postgres_changes", { event: "*", schema: "public", table: "user_data", filter: `user_id=eq.${userId}` }, () => onChange())
    .subscribe();
  return () => { try { supabase.removeChannel(channel); } catch (e) { /* ignore */ } };
}

// One row per account, holding the entire { profiles, activeId } blob as JSON, the same shape used for local storage.
// With end-to-end encryption on, `data` holds an encrypted envelope instead (see e2ee.js) and the server can't read it.
export async function pullCloudData(userId) {
  if (!syncConfigured) return null;
  const { data, error } = await supabase.from("user_data").select("data, plan, updated_at").eq("user_id", userId).maybeSingle();
  if (error || !data) return null;
  if (data.updated_at) stamps.set(userId, data.updated_at);
  if (isEnvelope(data.data)) {
    const rec = await getKeyRecord(userId);
    if (rec && rec.salt === data.data.salt) {
      try {
        const plain = await decryptJson(rec.key, data.data);
        setEncryptionFlag(userId, true);
        return { data: plain, plan: data.plan, encrypted: true };
      } catch (e) { await deleteKeyRecord(userId); }   // the stored key is stale (passphrase changed elsewhere)
    }
    setEncryptionFlag(userId, true);
    return { locked: true, envelope: data.data, plan: data.plan };
  }
  if (hasEncryptionFlag(userId)) { setEncryptionFlag(userId, false); await deleteKeyRecord(userId); }   // turned off on another device
  return data;
}

// Returns true (saved), false (error) or "locked" (the online copy is encrypted and this device hasn't unlocked it).
// Plain data is never allowed to overwrite an encrypted copy.
export async function pushCloudData(userId, payload) {
  if (!syncConfigured) return false;
  const rec = hasEncryptionFlag(userId) ? await getKeyRecord(userId) : null;
  if (hasEncryptionFlag(userId) && !rec) return "locked";

  const { data: cur } = await supabase.from("user_data").select("enc:data->_e2ee, salt:data->>salt").eq("user_id", userId).maybeSingle();
  const cloudEncrypted = cur?.enc === 1;
  let body = payload;
  if (rec) {
    if (cloudEncrypted && cur.salt !== rec.salt) { await deleteKeyRecord(userId); return "locked"; }   // passphrase changed on another device
    if (!cloudEncrypted && cur) { setEncryptionFlag(userId, false); await deleteKeyRecord(userId); }    // turned off elsewhere: follow
    else body = await encryptJson(rec.key, payload, rec);
  } else if (cloudEncrypted) {
    setEncryptionFlag(userId, true);
    return "locked";
  }
  const stamp = new Date().toISOString();
  const { error } = await supabase.from("user_data").upsert({ user_id: userId, data: body, updated_at: stamp });
  if (!error) stamps.set(userId, stamp);
  return !error;
}

async function uploadEncrypted(userId, passphrase, payload, remember) {
  const problem = passphraseProblem(passphrase);
  if (problem) throw Object.assign(new Error(problem), { code: "weak-passphrase" });
  const salt = randomSalt();
  const key = await deriveKey(passphrase, salt, KDF_ITERATIONS);
  const rec = { key, salt, iterations: KDF_ITERATIONS };
  const envelope = await encryptJson(key, payload, rec);
  const stamp = new Date().toISOString();
  const { error } = await supabase.from("user_data").upsert({ user_id: userId, data: envelope, updated_at: stamp });
  if (error) throw new Error("Couldn't save to your account. Check your connection and try again.");
  stamps.set(userId, stamp);
  await setKeyRecord(userId, rec, remember);
  setEncryptionFlag(userId, true);
}

// Turn encryption on (or change the passphrase): re-uploads the current data encrypted under a fresh salt and key.
export const enableEncryption = uploadEncrypted;

// Unlock an encrypted copy on this device. Returns the decrypted { profiles, activeId }.
export async function unlockEncryption(userId, passphrase, envelope, remember = true) {
  const iterations = envelope.iter || KDF_ITERATIONS;
  const key = await deriveKey(passphrase, envelope.salt, iterations);
  const data = await decryptJson(key, envelope);   // throws bad-passphrase
  await setKeyRecord(userId, { key, salt: envelope.salt, iterations }, remember);
  setEncryptionFlag(userId, true);
  return data;
}

// Turn encryption off: store the data in plain form again (so other devices need no passphrase).
export async function disableEncryption(userId, payload) {
  const stamp = new Date().toISOString();
  const { error } = await supabase.from("user_data").upsert({ user_id: userId, data: payload, updated_at: stamp });
  if (error) throw new Error("Couldn't save to your account. Check your connection and try again.");
  stamps.set(userId, stamp);
  await deleteKeyRecord(userId);
  setEncryptionFlag(userId, false);
}

export async function forgetEncryptionKey(userId) { await deleteKeyRecord(userId); setEncryptionFlag(userId, false); }

// Permanently deletes the account and its online data (server function, needs the signed-in session).
export async function deleteAccountOnServer() {
  if (!syncConfigured) throw new Error("Not connected to an account.");
  const { data, error } = await supabase.functions.invoke("delete-account", { body: { confirm: "DELETE" } });
  if (error) {
    let msg = error.message || "Couldn't delete the account.";
    try { const j = await error.context?.json?.(); if (j?.error || j?.message) msg = j.error || j.message; } catch (e) { /* keep default */ }
    throw new Error(msg);
  }
  if (data?.error) throw new Error(data.error);
  return true;
}
